import React from "react";
import { Box, Typography, Avatar, Paper, Rating, Button, useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import img1 from "../../assets/shop/Minerva.webp";
import img2 from "../../assets/shop/ghee_customer_Moment.webp";
import img3 from "../../assets/shop/Untitled_2_489afb6b.jpg";

const testimonials = [
    {
        name: "Aarav Sharma",
        review: "The birth chart reading was incredibly accurate! It helped me understand my career path better and make important life decisions with confidence.",
        image: img1,
    },
    {
        name: "Priya Patel",
        review: "Jyotish Jigyasa's predictions about my relationship timeline were spot on. The guidance provided clarity during a confusing phase of my life.",
        image: img2,
    },
    {
        name: "Dr. Rajesh Kumar",
        review: "As a skeptic, I was amazed by the precision of the planetary analysis. The remedies suggested actually brought positive changes in my professional life.",
        image: img3,
    },
];

const Testimonials = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Animation variants to match Courses component
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.3 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
        hover: {
            scale: 1.03,
            boxShadow: "0 8px 25px rgba(109, 22, 117, 0.2)",
            transition: { duration: 0.3 }
        }
    };

    return (
        <Box
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
        >
            <Box sx={{ 
                backgroundColor: "rgba(255, 234, 223, 0.3)",
                py: 8, 
                px: 2 
            }}>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                    {/* Header Section */}
                    <motion.div variants={itemVariants}>
                        <Typography
                            variant="h4"
                            align="center"
                            sx={{ 
                                fontWeight: "bold", 
                                color: "#6d1675", 
                                mb: 2,
                                fontSize: "30px",
                            }}
                        >
                            What Do Our <span style={{ color: "#ffc20f" }}>Clients Say</span>
                        </Typography>

                        {/* Flower Divider */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                maxWidth: 300,
                                mx: "auto",
                                mb: 6,
                            }}
                        >
                            <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                            <Box
                                sx={{
                                    px: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <LocalFloristIcon sx={{ fontSize: 28, color: "#ffc20f" }} />
                            </Box>
                            <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                        </Box>
                    </motion.div>

                    {/* Testimonial Cards */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 4,
                            flexWrap: { xs: "nowrap", md: "wrap" },
                            overflowX: { xs: "auto", md: "visible" },
                            scrollbarWidth: "none",
                            px: { xs: 1, md: 0 },
                            justifyContent: "center",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        {testimonials.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={cardVariants}
                                whileHover="hover"
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: "2px solid #f0f0f0",
                                        borderRadius: "16px",
                                        padding: "28px",
                                        width: { xs: 300, md: 300 },
                                        backgroundColor: "#fff",
                                        boxShadow: "0 4px 15px rgba(109, 22, 117, 0.1)",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        textAlign: "center",
                                        minHeight: 280,
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            borderColor: "#ffc20f",
                                        },
                                    }}
                                >
                                    {/* Quote Icon */}
                                    <Box
                                        sx={{
                                            color: "#6d1675",
                                            fontSize: "32px",
                                            mb: 2,
                                            opacity: 0.7,
                                        }}
                                    >
                                        "
                                    </Box>

                                    {/* Review Text */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: "15px",
                                            color: "#666",
                                            mb: 3,
                                            lineHeight: 1.6,
                                            fontStyle: "italic",
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {item.review}
                                    </Typography>

                                    {/* Customer Info */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 2,
                                            mt: "auto",
                                        }}
                                    >
                                        <Avatar 
                                            src={item.image} 
                                            sx={{ 
                                                width: 60, 
                                                height: 60,
                                                border: "3px solid #ffc20f",
                                                boxShadow: "0 4px 12px rgba(109, 22, 117, 0.2)",
                                            }} 
                                        />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    color: "#6d1675",
                                                    fontSize: "16px",
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                            <Rating 
                                                value={5} 
                                                readOnly 
                                                sx={{ 
                                                    color: "#ffc20f",
                                                    "& .MuiRating-icon": {
                                                        fontSize: "20px"
                                                    }
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        ))}
                    </Box>

                    {/* Additional Call-to-Action */}
                    <motion.div variants={itemVariants}>
                        <Box sx={{ textAlign: "center", mt: 6 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderColor: "#6d1675",
                                    color: "#6d1675",
                                    fontWeight: "bold",
                                    borderRadius: 50,
                                    fontSize: "16px",
                                    textTransform: "none",
                                    "&:hover": {
                                        borderColor: "#ffc20f",
                                        backgroundColor: "#6d1675",
                                        color: "#fff",
                                    },
                                }}
                            >
                                Read More Testimonials
                            </Button>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default Testimonials;